import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmActionModal } from '../surveys/results/ConfirmActionModal';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { computeDisplayName } from '../../types/contact';
import { getContact, softDeleteContact } from './api';
import { listAccounts } from '../accounts/api';
import { getBroadcast } from '../broadcasts';
import type { Contact } from '../../types/contact';

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const contact = useQuery({
    queryKey: ['contact', id],
    queryFn: () => getContact(id!),
    enabled: !!id,
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => softDeleteContact(id!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.removeQueries({ queryKey: ['contact', id] });
      navigate('/contacts');
    },
  });

  if (contact.isLoading) return <CenteredSpinner />;
  if (contact.error || !contact.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load contact: {(contact.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  const c = contact.data;
  const accountById = new Map((accounts.data ?? []).map((a) => [a.id, a]));

  return (
    <div className="space-y-4 max-w-4xl">
      <PageHeader
        title={computeDisplayName(c) || '(no name)'}
        description={`${c.email ?? '—'} · ${c.phone ?? '—'}`}
        backHref="/contacts"
        actions={
          user.role === 'admin' ? (
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={`/contacts/${c.id}/edit`}>Edit</Link>
              </Button>
              <Button variant="outline" onClick={() => setConfirmingDelete(true)}>
                Delete contact
              </Button>
            </div>
          ) : undefined
        }
      />

      {confirmingDelete ? (
        <ConfirmActionModal
          title={`Delete ${computeDisplayName(c) || 'this contact'}?`}
          body={
            <>
              <p>
                This removes {computeDisplayName(c) || 'this contact'} from contact lists, search and
                broadcasts, and ends any client portal access they had. An admin can recover it
                afterwards.
              </p>
              {remove.error ? (
                <p className="mt-2 text-red-600">{(remove.error as Error).message}</p>
              ) : null}
            </>
          }
          confirmLabel="Delete contact"
          destructive
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmingDelete(false)}
        />
      ) : null}

      <section className="bg-white rounded-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Identity</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-slate-500">Title</dt>
          <dd>{c.title ?? '—'}</dd>
          <dt className="text-slate-500">First name</dt>
          <dd>{c.firstName}</dd>
          <dt className="text-slate-500">Last name</dt>
          <dd>{c.lastName ?? '—'}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd>{c.email ?? '—'}</dd>
          <dt className="text-slate-500">Phone</dt>
          <dd>{c.phone ?? '—'}</dd>
          <dt className="text-slate-500">Birthday</dt>
          <dd>{c.birthday ? `${c.birthday.day}/${c.birthday.month}` : '—'}</dd>
          {/* Stage 3 §1.1. Show channel opt-ins as part of identity --
              they're contact-level, not per-account-link. The per-link
              "Campaign previews" toggle lives in the Linked accounts
              section below. */}
          <dt className="text-slate-500">Comms channels</dt>
          <dd className="flex flex-wrap gap-2">
            {c.channelEmail && c.email ? (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                📧 Email
              </span>
            ) : null}
            {c.channelSMS && c.phone ? (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700">
                📱 SMS
              </span>
            ) : null}
            {!(c.channelEmail && c.email) && !(c.channelSMS && c.phone) ? (
              <span className="text-slate-400">None</span>
            ) : null}
          </dd>
        </dl>
        {c.notes && (
          <div className="mt-4">
            <p className="text-sm text-slate-500 mb-1">Notes</p>
            <pre className="whitespace-pre-wrap text-sm font-sans">{c.notes}</pre>
          </div>
        )}
      </section>

      {/* Stage 6A v3 §2.8 — per-channel reachability + unsubscribe history. */}
      <section className="bg-white rounded-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Communication preferences</h2>
        <div className="space-y-4 text-sm">
          <ChannelPref
            label="Email"
            enabled={c.channelEmail && Boolean(c.email)}
            missingDestination={!c.email}
            missingLabel="no email address"
            unsubscribedAt={c.channelEmailUnsubscribedAt}
            fromBroadcastId={c.channelEmailUnsubscribedFromBroadcastId}
            resubscribedAt={c.channelEmailResubscribedAt}
          />
          <ChannelPref
            label="SMS"
            enabled={c.channelSMS && Boolean(c.phone)}
            missingDestination={!c.phone}
            missingLabel="no phone number"
            unsubscribedAt={c.channelSMSUnsubscribedAt}
            fromBroadcastId={c.channelSMSUnsubscribedFromBroadcastId}
            resubscribedAt={c.channelSMSResubscribedAt}
          />
        </div>
      </section>

      <section className="bg-white rounded-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Linked accounts ({c.accountLinks.length})</h2>
        {c.accountLinks.length === 0 ? (
          <p className="text-sm text-slate-500">No account links.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-4 font-medium">Account</th>
                <th className="py-2 pr-4 font-medium">Job title</th>
                <th className="py-2 pr-4 font-medium">Primary</th>
                <th className="py-2 pr-4 font-medium">Service comms</th>
                <th className="py-2 pr-4 font-medium">Billing comms</th>
                <th className="py-2 pr-4 font-medium">Previews</th>
              </tr>
            </thead>
            <tbody>
              {c.accountLinks.map((l) => {
                const a = accountById.get(l.accountId);
                return (
                  <tr key={l.accountId} className="border-t border-slate-100">
                    <td className="py-2 pr-4">
                      {a ? (
                        <Link to={`/accounts/${a.id}`} className="text-blue-600 hover:underline">
                          {a.name}
                        </Link>
                      ) : (
                        <span className="text-slate-500 font-mono">{l.accountId}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">{l.jobTitle ?? '—'}</td>
                    <td className="py-2 pr-4">{l.isPrimary ? '✓' : ''}</td>
                    <td className="py-2 pr-4">{l.receivesServiceCommunications ? '✓' : ''}</td>
                    <td className="py-2 pr-4">{l.receivesBillingCommunications ? '✓' : ''}</td>
                    <td className="py-2 pr-4">{l.receivesCampaignPreviews ? '✓' : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white rounded-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold mb-4">History</h2>
        <p className="text-sm text-slate-500">Audit log view lands in P4.</p>
      </section>
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// One channel's reachability + unsubscribe/resubscribe history (§2.8). The
// unsubscribe line links the attributing broadcast's title when present.
function ChannelPref({
  label,
  enabled,
  missingDestination,
  missingLabel,
  unsubscribedAt,
  fromBroadcastId,
  resubscribedAt,
}: {
  label: string;
  enabled: boolean;
  missingDestination: boolean;
  missingLabel: string;
  unsubscribedAt: Contact['channelEmailUnsubscribedAt'];
  fromBroadcastId: Contact['channelEmailUnsubscribedFromBroadcastId'];
  resubscribedAt: Contact['channelEmailResubscribedAt'];
}) {
  return (
    <div>
      <p className="font-medium text-slate-800">{label}</p>
      <ul className="mt-1 space-y-0.5">
        <li className={enabled ? 'text-emerald-700' : 'text-slate-500'}>
          {enabled ? '☑' : '☐'} {label} {enabled ? 'enabled' : 'disabled'}
          {missingDestination && <span className="text-slate-400"> ({missingLabel})</span>}
        </li>
        {unsubscribedAt && (
          <li className="text-red-600">
            ✗ Unsubscribed {fmtDate(unsubscribedAt)}
            {fromBroadcastId && (
              <>
                {' '}
                (from <BroadcastTitleLink broadcastId={fromBroadcastId} />)
              </>
            )}
          </li>
        )}
        {resubscribedAt && (
          <li className="text-slate-600">↻ Resubscribed {fmtDate(resubscribedAt)}</li>
        )}
      </ul>
    </div>
  );
}

// Best-effort broadcast-title link. The broadcast read is visibility-gated, so
// a staff user who can't see the attributing broadcast still gets a sensible
// fallback rather than an error.
function BroadcastTitleLink({ broadcastId }: { broadcastId: string }) {
  const broadcast = useQuery({
    queryKey: ['broadcast', broadcastId],
    queryFn: () => getBroadcast(broadcastId),
    retry: false,
  });
  if (broadcast.data) {
    return (
      <Link to={`/broadcasts/${broadcastId}`} className="font-medium text-blue-600 hover:underline">
        “{broadcast.data.title}”
      </Link>
    );
  }
  // Loading or inaccessible — link to the broadcast without the title.
  return (
    <Link to={`/broadcasts/${broadcastId}`} className="text-blue-600 hover:underline">
      a broadcast
    </Link>
  );
}
